import {
  URN_AUTH_INVALID_TOKEN,
  URN_AUTH_RATE_LIMITED,
  URN_QUOTA_BUDGET,
  URN_TOMTOM_UPSTREAM,
} from '@clearroute/shared'

import { parseProblem } from './problem'

/**
 * Problem-URN aware fetch wrapper.
 *
 * Mirrors 02-RESEARCH.md §Code Examples Pattern 7 (lines 599-651) with two
 * additions called out in Plan 02-01 Task 2:
 *
 *   1. The `x-new-token` response header is read BEFORE the body, so silent
 *      JWT rotation survives error paths as well as success (D-23 + D-85,
 *      mitigates T-2-JWT race where an error response races a rotated token).
 *   2. `URN_AUTH_INVALID_TOKEN` forces `localStorage.removeItem(JWT_KEY)`
 *      ahead of the throw — the next navigation lands on SignIn rather than
 *      re-sending the poisoned JWT (D-84).
 *
 * `ApiError` carries the parsed `{ urn, status, detail }`; `classifyError`
 * narrows it to the four user-visible buckets `SRCH-02` consumes (D-83).
 */

export const JWT_KEY = 'clearroute:jwt'

const BASE_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) ||
  'https://clearroute-api.blanitavlad.workers.dev'

const URN_UNKNOWN = 'urn:clearroute:error:unknown'

export class ApiError extends Error {
  readonly urn: string
  readonly status: number
  readonly detail?: string

  constructor(urn: string, status: number, detail?: string) {
    super(detail ?? urn)
    this.name = 'ApiError'
    this.urn = urn
    this.status = status
    this.detail = detail
  }
}

function buildUrl(path: string, query?: Record<string, unknown>): URL {
  // URL constructor requires an absolute base; path may already be absolute
  // (passed as `/auth/request`), so we resolve against BASE_URL.
  const url = new URL(path, BASE_URL)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue
      url.searchParams.set(key, String(value))
    }
  }
  return url
}

function readJwt(): string | null {
  try {
    return localStorage.getItem(JWT_KEY)
  } catch {
    return null
  }
}

function persistJwt(value: string): void {
  try {
    localStorage.setItem(JWT_KEY, value)
  } catch {
    // localStorage might be disabled (Safari private mode); swallow — the
    // next request simply re-auths. No user-facing error needed.
  }
}

function clearJwt(): void {
  try {
    localStorage.removeItem(JWT_KEY)
  } catch {
    // ignore — see persistJwt note.
  }
}

async function handleResponse(res: Response): Promise<unknown> {
  // Silent JWT rotation — honoured on BOTH 2xx and error paths (D-23).
  const fresh = res.headers.get('x-new-token')
  if (fresh) persistJwt(fresh)

  if (!res.ok) {
    const raw = await res.json().catch(() => null)
    const parsed = parseProblem(raw)
    if (parsed) {
      if (parsed.type === URN_AUTH_INVALID_TOKEN) clearJwt()
      throw new ApiError(parsed.type, parsed.status, parsed.detail)
    }
    throw new ApiError(URN_UNKNOWN, res.status)
  }

  // 204 No Content: no body to parse.
  if (res.status === 204) return null
  return res.json()
}

export async function apiGet(
  path: string,
  query?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<unknown> {
  const url = buildUrl(path, query)
  const jwt = readJwt()
  const res = await fetch(url, {
    signal,
    headers: jwt ? { authorization: `Bearer ${jwt}` } : {},
  })
  return handleResponse(res)
}

export async function apiPost(
  path: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<unknown> {
  const url = buildUrl(path)
  const jwt = readJwt()
  const headers: Record<string, string> = {
    'content-type': 'application/json',
  }
  if (jwt) headers.authorization = `Bearer ${jwt}`
  const res = await fetch(url, {
    method: 'POST',
    signal,
    headers,
    body: JSON.stringify(body ?? {}),
  })
  return handleResponse(res)
}

export type ErrorClass = 'budget' | 'upstream' | 'auth' | 'unknown'

export function classifyError(e: unknown): ErrorClass {
  if (e instanceof ApiError) {
    if (e.urn === URN_QUOTA_BUDGET) return 'budget'
    if (e.urn === URN_TOMTOM_UPSTREAM) return 'upstream'
    if (e.urn === URN_AUTH_INVALID_TOKEN) return 'auth'
    if (e.urn === URN_AUTH_RATE_LIMITED) return 'auth'
  }
  return 'unknown'
}
